/**
 * Phase 1 durable damage facts.
 *
 * The ledger is owned by one DemolitionRuntime. It is intentionally capped and
 * immutable at the record boundary so the renderer, evaluation bridge, and
 * future replay tooling all consume the same accepted damage facts.
 */

export type DamageFactKind = 'kinetic' | 'blast' | 'contact-load';

export interface DamageFactPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface DamageFactTangent {
  readonly x: number;
  readonly y: number;
}

export interface DamageFact {
  /**
   * Reset epoch; sequences are only unique within one epoch. Ledger-authored
   * facts always set this. Optionality keeps legacy fixtures/serialized facts
   * source-compatible while they are migrated.
   */
  readonly epoch?: number;
  readonly sequence: number;
  readonly simTime: number;
  readonly kind: DamageFactKind;
  /** Wall-local/rest-space position, not a mutable body transform. */
  readonly position: DamageFactPoint;
  readonly outwardNormal: DamageFactPoint;
  readonly radius: number;
  readonly appliedEnergy: number;
  readonly normalizedSeverity: number;
  readonly cellId?: number;
  readonly damageBefore: number;
  readonly damageAfter: number;
  readonly topologyChanged: boolean;
  /**
   * Unit incoming velocity projected onto the struck wall face (2D in that
   * face's plane). Fallback (1, 0) when the hit is degenerate / head-on.
   * Optional so older fixtures and snapshot consumers stay valid.
   */
  readonly impactTangent?: DamageFactTangent;
  /** sin(angle from face normal): 0 = head-on, 1 = fully in-plane. */
  readonly impactGrazing?: number;
  /** Crushed volume (m³) from this event — not spawned as rigid bodies. */
  readonly pulverizedVolume?: number;
}

export type DamageFactInput = Omit<DamageFact, 'epoch' | 'sequence'>;

export interface DemolitionDamageLedgerTelemetry {
  readonly epoch: number;
  readonly appended: number;
  readonly droppedFacts: number;
  readonly retainedStart: number | null;
  readonly retainedEnd: number | null;
}

export interface DemolitionDamageLedger {
  readonly capacity: number;
  readonly epoch: number;
  readonly facts: readonly DamageFact[];
  append(input: DamageFactInput): DamageFact;
  telemetry(): DemolitionDamageLedgerTelemetry;
  reset(): void;
}

const DEFAULT_CAPACITY = 256;

function checkedCapacity(capacity: number): number {
  if (!Number.isSafeInteger(capacity) || capacity < 1) {
    throw new RangeError('Demolition damage-ledger capacity must be a positive safe integer');
  }
  return capacity;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

function point(value: DamageFactPoint): DamageFactPoint {
  return Object.freeze({
    x: Number.isFinite(value.x) ? value.x : 0,
    y: Number.isFinite(value.y) ? value.y : 0,
    z: Number.isFinite(value.z) ? value.z : 0,
  });
}

function tangent2(value: DamageFactTangent | undefined): DamageFactTangent | undefined {
  if (!value) return undefined;
  return Object.freeze({
    x: Number.isFinite(value.x) ? value.x : 1,
    y: Number.isFinite(value.y) ? value.y : 0,
  });
}

/**
 * Project incoming velocity onto the dominant wall face at `restPosition`.
 * Returns a unit 2D tangent in that face's plane plus a 0..1 grazing factor.
 */
export function projectImpactOntoWall(
  restPosition: DamageFactPoint,
  incomingVelocity: DamageFactPoint,
  halfExtents: DamageFactPoint,
): { tangent: DamageFactTangent; grazing: number } {
  const hx = Math.max(Math.abs(halfExtents.x), 1e-6);
  const hy = Math.max(Math.abs(halfExtents.y), 1e-6);
  const hz = Math.max(Math.abs(halfExtents.z), 1e-6);
  const nx = Math.abs(restPosition.x) / hx;
  const ny = Math.abs(restPosition.y) / hy;
  const nz = Math.abs(restPosition.z) / hz;
  let tx: number;
  let ty: number;
  if (nz >= nx && nz >= ny) {
    tx = incomingVelocity.x;
    ty = incomingVelocity.y;
  } else if (nx >= ny) {
    tx = incomingVelocity.y;
    ty = incomingVelocity.z;
  } else {
    tx = incomingVelocity.x;
    ty = incomingVelocity.z;
  }
  const planar = Math.hypot(tx, ty);
  const speed = Math.hypot(incomingVelocity.x, incomingVelocity.y, incomingVelocity.z);
  const grazing = speed > 1e-6 ? Math.min(1, planar / speed) : 0;
  if (planar < 1e-4) return { tangent: { x: 1, y: 0 }, grazing };
  return { tangent: { x: tx / planar, y: ty / planar }, grazing };
}

/** Create an isolated bounded ledger for one demolition structure. */
export function createDemolitionDamageLedger(
  capacity: number = DEFAULT_CAPACITY,
): DemolitionDamageLedger {
  const maxRecords = checkedCapacity(capacity);
  let epoch = 0;
  let nextSequence = 0;
  let appended = 0;
  let droppedFacts = 0;
  let records: DamageFact[] = [];

  return {
    capacity: maxRecords,

    get epoch() {
      return epoch;
    },

    get facts() {
      return Object.freeze(records.slice());
    },

    append(input: DamageFactInput): DamageFact {
      nextSequence += 1;
      appended += 1;
      const fact = Object.freeze({
        epoch,
        sequence: nextSequence,
        simTime: Number.isFinite(input.simTime) ? input.simTime : 0,
        kind: input.kind,
        position: point(input.position),
        outwardNormal: point(input.outwardNormal),
        radius: Math.max(0, Number.isFinite(input.radius) ? input.radius : 0),
        appliedEnergy: Math.max(0, Number.isFinite(input.appliedEnergy) ? input.appliedEnergy : 0),
        normalizedSeverity: clamp01(input.normalizedSeverity),
        ...(input.cellId === undefined ? {} : { cellId: input.cellId }),
        damageBefore: clamp01(input.damageBefore),
        damageAfter: clamp01(input.damageAfter),
        topologyChanged: input.topologyChanged === true,
        ...(input.impactTangent
          ? { impactTangent: tangent2(input.impactTangent) }
          : {}),
        ...(input.impactGrazing === undefined
          ? {}
          : { impactGrazing: clamp01(input.impactGrazing) }),
        ...(input.pulverizedVolume === undefined
          ? {}
          : {
              pulverizedVolume: Math.max(
                0,
                Number.isFinite(input.pulverizedVolume) ? input.pulverizedVolume : 0,
              ),
            }),
      }) as DamageFact;
      records.push(fact);
      if (records.length > maxRecords) {
        droppedFacts += records.length - maxRecords;
        records = records.slice(-maxRecords);
      }
      return fact;
    },

    telemetry(): DemolitionDamageLedgerTelemetry {
      const telemetry = {
        epoch,
        appended,
        droppedFacts,
        retainedStart: records[0]?.sequence ?? null,
        retainedEnd: records[records.length - 1]?.sequence ?? null,
      };
      return Object.freeze(telemetry);
    },

    reset(): void {
      epoch += 1;
      nextSequence = 0;
      appended = 0;
      droppedFacts = 0;
      records = [];
    },
};
}
